import { useState, useEffect, useSyncExternalStore, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { contactCustomerSupport } from '@goldcast/api/backend';
import { getRemainingProcessingTimeInSec } from '../VideoImportPage/utils';
import { ContentErrorTypeMap, ContentStatesEnum } from '../VideoImportPage/constants';
import VideoImportProgressToast from '../VideoImportPage/VideoImportProgressToast';
import { ErrorConfig } from './types';
import { classnames } from '@/libs/utils';
import useContentUpload from '@/hooks/useContentUpload';
import { CustomEvents } from '@/libs/eventBus/constants';
import { usePusherContext } from '@/context/PusherContext/PusherContext';
import EventBus from '@/libs/eventBus/eventBus';
import AlertDialog from '@/components/organisms/AlertDialog';
import { disableFirstTimeUser, firstTimeUserStore } from '@/stores/firstTimeUserStore';
import { handleConfettiState, removeContentUploadEntry } from '@/stores/contentUploadStore';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { currentUser } from '@/stores/user';
import useAnalytics from '@/hooks/useAnalytics';

const SESSION_STORAGE_PROCESSING_COMPLETE_TIMES = 'processingCompleteTimes';

export default function VideoImportProcessingPage() {
  const { broadcastId } = useParams<{ broadcastId: string }>();
  const [currentProcessingTime, setCurrentProcessingTime] = useState<number | null>(null);
  const isFirstTimeUser = useSyncExternalStore(firstTimeUserStore.subscribe, firstTimeUserStore.getSnapshot);

  const {
    getContentUploadById,
    updateContentState,
    changeContentUploadStatus,
    cancelUpload,
    client,
    trackFilestackError
  } = useContentUpload();
  const { trackTranscriptionRestricted } = useAnalytics();

  const navigate = useNavigate();

  const contentUpload = getContentUploadById(broadcastId);
  const { contentState, projectId, contentId, totalPercentageUploaded, error } = contentUpload;
  const { isPusherConnected } = usePusherContext();

  const handleErrorConfirmation = useCallback(() => {
    removeContentUploadEntry(contentId);
    navigate('/');
  }, [contentId, navigate]);

  const handleCancelUpload = useCallback(() => {
    cancelUpload(contentId);
  }, [contentId, cancelUpload]);

  const handleContactSales = () => {
    contactCustomerSupport({
      id: currentUser.getSnapshot()?.organization as string,
      body: {
        text: 'Customer want to use Multi-language support in Content Lab.'
      }
    })
      .then(() => {
        trackTranscriptionRestricted();
        showSuccessToast('We have received your request. Our team will get in touch with you soon.');
      })
      .catch(error => {
        showErrorToast(JSON.parse(error)[0]?.message || 'An error occurred!');
      });
  };

  const defaultErrorConfig: ErrorConfig = {
    icon: 'error' as const,
    primaryActionTitle: 'Back to Home',
    primaryActionCTA: handleErrorConfirmation
  };

  const errorConfig: Record<string, ErrorConfig> = {
    TRANSCRIPTION_SUPPORT_RESTRICTED: {
      icon: 'lock',
      primaryActionTitle: 'Contact Goldcast',
      primaryActionCTA: handleContactSales,
      secondaryActionTitle: 'Back to Home',
      secondaryActionCTA: handleErrorConfirmation
    },
    PROCESSING_FAILED: defaultErrorConfig,
    GENERATING_FAILED: defaultErrorConfig,
    UPLOAD_FAILED: defaultErrorConfig
  };

  const getErrorConfig = (errorType?: string) => {
    if (!errorType) {
      return defaultErrorConfig;
    }
    return errorConfig[errorType] || defaultErrorConfig;
  };

  useEffect(() => {
    client.on('upload.error', trackFilestackError);
    return () => {
      client.off('upload.error', trackFilestackError);
    };
  }, [client, trackFilestackError]);

  useEffect(() => {
    if (
      contentState === ContentStatesEnum.Processing &&
      contentUpload.videoProcessingTime &&
      contentUpload.contentUploadedTime
    ) {
      const uploadedTime = new Date(contentUpload.contentUploadedTime);
      const remainingSeconds = Number(contentUpload.videoProcessingTime);
      const processingTimeInterval = setInterval(() => {
        const remainingProcessingTime = getRemainingProcessingTimeInSec(uploadedTime, remainingSeconds);
        setCurrentProcessingTime(remainingProcessingTime);
      }, 1000);

      return () => {
        clearInterval(processingTimeInterval);
      };
    }
  }, [contentUpload.videoProcessingTime, contentUpload.contentUploadedTime, contentState]);

  useEffect(() => {
    if (!broadcastId) {
      return;
    }

    if (contentState === ContentStatesEnum.Generating && contentUpload.clipGenerationEstimatedTime) {
      setCurrentProcessingTime(null);

      const processingCompleteTimes = JSON.parse(
        sessionStorage.getItem(SESSION_STORAGE_PROCESSING_COMPLETE_TIMES) || '{}'
      );
      const storedProcessingCompleteTime = processingCompleteTimes[broadcastId];
      let processingCompleteTime = new Date();

      if (storedProcessingCompleteTime) {
        processingCompleteTime = new Date(storedProcessingCompleteTime);
      } else {
        processingCompleteTimes[broadcastId] = processingCompleteTime;
        sessionStorage.setItem(SESSION_STORAGE_PROCESSING_COMPLETE_TIMES, JSON.stringify(processingCompleteTimes));
      }

      const remainingSeconds = Number(contentUpload.clipGenerationEstimatedTime);
      const processingTimeInterval = setInterval(() => {
        const remainingProcessingTime = getRemainingProcessingTimeInSec(processingCompleteTime, remainingSeconds);
        setCurrentProcessingTime(remainingProcessingTime);
      }, 1000);

      return () => {
        clearInterval(processingTimeInterval);
      };
    }
  }, [contentUpload.clipGenerationEstimatedTime, contentState, broadcastId]);

  useEffect(() => {
    if (contentState === ContentStatesEnum.Cancelled) {
      navigate('/');
      return;
    }
  }, [contentState]);

  useEffect(() => {
    if (!isPusherConnected) {
      return;
    }
    const contentProcessingListener = EventBus.on(CustomEvents.TranscriptionStatusUpdated, data =>
      changeContentUploadStatus(data, ContentStatesEnum.Processing, contentId)
    );
    const clipGenerationListener = EventBus.on(CustomEvents.ClipGenerationStatusUpdated, data =>
      changeContentUploadStatus(data, ContentStatesEnum.Generating, contentId)
    );
    const routeChangeListener = EventBus.on(CustomEvents.NavigateToContentLabHome, () => navigate('/'));
    return () => {
      if (contentProcessingListener) {
        EventBus.off(CustomEvents.TranscriptionStatusUpdated, contentProcessingListener);
      }
      if (clipGenerationListener) {
        EventBus.off(CustomEvents.ClipGenerationStatusUpdated, clipGenerationListener);
      }
      if (routeChangeListener) {
        EventBus.off(CustomEvents.NavigateToContentLabHome, routeChangeListener);
      }
    };
  }, [isPusherConnected, changeContentUploadStatus, contentId, navigate]);

  useEffect(() => {
    if (contentState !== ContentStatesEnum.Uploading && isFirstTimeUser) {
      disableFirstTimeUser();
    }
  }, [contentState, isFirstTimeUser]);

  useEffect(() => {
    if (contentState !== ContentStatesEnum.GeneratingCompleted) {
      return;
    }
    handleConfettiState({ showConfetti: true, contentId });
    updateContentState(ContentStatesEnum.ContentReadyForPreview, contentId);
  }, [contentState, projectId, contentId, navigate, updateContentState]);

  return (
    <>
      <div className="pointer-events-none flex size-full overflow-hidden bg-slate-100">
        <div
          className={classnames(
            'relative mt-1 flex h-full grow flex-col overflow-hidden transition-all duration-[400ms] ease-in-out',
            { 'pl-8': !isFirstTimeUser }
          )}
        >
          <div
            className={classnames({
              'w-full py-3': true,
              'pt-6': contentState !== ContentStatesEnum.Uploading
            })}
          >
            <VideoImportProgressToast
              type={ContentStatesEnum.Uploading}
              title="Uploading"
              totalPercentageUploaded={totalPercentageUploaded}
              message="Your upload will continue smoothly even if you navigate away from this page."
              onCancel={handleCancelUpload}
              isVisible={contentState === ContentStatesEnum.Uploading}
              activeState={contentState}
            />
            <VideoImportProgressToast
              type={ContentStatesEnum.Processing}
              title="Processing & Analyzing"
              message="We're transcribing your video and extracting key insights."
              isVisible={[ContentStatesEnum.Uploading, ContentStatesEnum.Processing].includes(contentState)}
              activeState={contentState}
              currentProcessingTime={currentProcessingTime}
            />
            <div
              className={classnames(
                'flex -translate-y-12 justify-center',
                contentState === ContentStatesEnum.Uploading
                  ? 'scale-[0.94]'
                  : contentState === ContentStatesEnum.Processing
                    ? 'scale-[0.97]'
                    : 'pt-6'
              )}
            >
              <VideoImportProgressToast
                type={ContentStatesEnum.Generating}
                title="Generating Assets"
                message="Almost there! We're flexing our AI muscles to create engaging assets for you."
                isVisible={[
                  ContentStatesEnum.Uploading,
                  ContentStatesEnum.Processing,
                  ContentStatesEnum.Generating
                ].includes(contentState)}
                activeState={contentState}
                currentProcessingTime={currentProcessingTime}
              />
              <VideoImportProgressToast
                type={ContentStatesEnum.ContentReadyForPreview}
                title="Clips Ready"
                message="Your Clips are ready for preview. We'll wait for you to finish up."
                isVisible={ContentStatesEnum.ContentReadyForPreview === contentState}
                activeState={contentState}
              />
            </div>
          </div>
        </div>
      </div>
      <AlertDialog
        isOpen={!!error?.type}
        heading={error?.type ? ContentErrorTypeMap[error.type] : 'Error'}
        message={error?.message}
        type={getErrorConfig(error?.type).icon}
        primaryActionTitle={getErrorConfig(error?.type).primaryActionTitle}
        handlePrimaryActionClick={getErrorConfig(error?.type).primaryActionCTA}
        secondaryActionTitle={getErrorConfig(error?.type).secondaryActionTitle}
        handleSecondaryActionClick={getErrorConfig(error?.type).secondaryActionCTA}
      />
    </>
  );
}
